"use client";

import React from "react";
import {
  useReactTable,
  getCoreRowModel,
  ColumnDef,
  flexRender,
  getPaginationRowModel,
  type PaginationState,
} from "@tanstack/react-table";
import styles from "./index.module.scss";
import type { Transaction } from "@/types/bridge-scanner.types";
import { FaArrowRightLong } from "react-icons/fa6";
import classNames from "classnames";
import type { HexString } from "@/types/general.types";
import { useParams, useRouter } from "next/navigation";
import { resolveChainFromId } from "@/utils/resolve-chain.util";
import BlessSkeletonLoader from "@/components/layout/skeleton-loader-v2";
import useBridgeScannerQuery from "@/features/bridge-scanner/hooks/useBridgeScannerTransactions";
import TransactionStatus from "@/features/bridge-scanner/components/status";
import CopyableText from "@/features/bridge-scanner/components/copyable-address";
import Chip from "@/features/bridge-scanner/components/type";
import TransactionTimestamp from "@/features/bridge-scanner/components/time";
import LeftControls from "@/features/bridge-scanner/components/table/left-controls";
import TablePaginator from "@/features/bridge-scanner/components/table/paginator";
import NoTransactionFound from "@/features/bridge-scanner/components/no-tx-found";
import TransactionCard from "@/features/bridge-scanner/components/table/transaction-card";

const columns: ColumnDef<Transaction>[] = [
  {
    accessorKey: "status",
    header: "Status",
    size: 100,
    cell: ({ getValue }) => {
      return (
        <div className={styles.status}>
          <TransactionStatus status={getValue() as Transaction["status"]} />
        </div>
      );
    },
  },
  {
    accessorKey: "originTxnHash",
    id: "txHash",
    header: "Tx Hash",
    size: 200,
    cell: ({ getValue, row }) => {
      return (
        <div style={{ maxWidth: 250, overflowX: "hidden" }}>
          <CopyableText
            hideIcon
            text={getValue() as string}
            count={12}
            preventConcatenation
          />
        </div>
      );
    },
  },
  {
    accessorKey: "type",
    header: "Type",
    size: 50,
    meta: {
      className: styles.hideableColumn,
    },
    cell: ({ getValue }) => {
      return <Chip text={getValue() as Transaction["type"]} />;
    },
  },
  {
    id: "from",
    accessorKey: "fromAddress",
    header: "From",
    size: 100,
    cell: ({ getValue, row }) => {
      const chain = resolveChainFromId(
        row.original.fromChain,
        row.original.network,
      );

      return (
        <div className={styles.fromCell}>
          <CopyableText
            icon={chain?.logo}
            iconTooltipText={chain?.chainName || ""}
            text={getValue() as string}
          />
          <div className={styles.arrow}>
            <FaArrowRightLong />
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "toAddress",
    header: "To",
    size: 100,
    cell: ({ getValue, row }) => {
      const chain = resolveChainFromId(
        row.original.toChain,
        row.original.network,
      );
      return (
        <CopyableText
          icon={chain?.logo}
          iconTooltipText={chain?.chainName || ""}
          text={getValue() as string}
        />
      );
    },
  },
  {
    accessorKey: "originTimestamp",
    header: "Time",
    size: 200,
    meta: {
      className: styles.hideableColumn,
    },
    cell: ({ getValue }) => {
      return <TransactionTimestamp timestamp={getValue() as string} />;
    },
  },
];

const BridgeTransactionsTable = () => {
  const { network } = useParams() as { network: string };
  const router = useRouter();

  const tableQuery = useBridgeScannerQuery();
  const { data, isLoading, isFetched } = tableQuery;

  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const table = useReactTable({
    data: data ?? [],
    columns,
    defaultColumn: {
      size: 350,
      minSize: 20,
      maxSize: 350,
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    state: {
      pagination,
    },
    autoResetPageIndex: false,
  });

  const handleGoTo = (txHash: HexString) => {
    router.push(`/bridge-scanner/${network}/tx/${txHash}`);
  };

  return (
    <>
      <div className={styles.tableHeaderArea}>
        <LeftControls resetTableIndex={table.resetPageIndex} />
        <TablePaginator table={table} queryControl={tableQuery} />
      </div>
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className={styles.headerRow}>
                {headerGroup.headers.map((header) => {
                  const meta = header.column.columnDef.meta;
                  return (
                    <th
                      key={header.id}
                      className={classNames(styles.header, meta?.className)}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          {!isLoading && (
            <tbody className={styles.rowWrapper}>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className={classNames(styles.row)}
                  onClick={() => handleGoTo(row.getValue("txHash"))}
                >
                  {row.getVisibleCells().map((cell) => {
                    const meta = cell.column.columnDef.meta;
                    return (
                      <td
                        key={cell.id}
                        className={classNames(styles.cell, meta?.className)}
                        style={{ width: cell.column.getSize() }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>

      {isFetched && !data?.length && (
        <NoTransactionFound
          showResetFiltersBtn
          text="Hmmm, nothing seems to match..."
        />
      )}

      {!isLoading && (
        <div className={styles.transactionCardsContainer}>
          {table?.getRowModel().rows.map((tx) => (
            <TransactionCard key={tx.original.originTxnHash} tx={tx.original} />
          ))}
        </div>
      )}

      {isLoading && (
        <div className={styles.loaderWrapper}>
          {Array.from({ length: 8 }).map((p, i) => {
            return <BlessSkeletonLoader key={i} height={90} />;
          })}
        </div>
      )}
    </>
  );
};

export default BridgeTransactionsTable;
